import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { AuthContext } from "../../../../context/authcontext";
import { ChatService } from "./chatService";
import { chatWebSocketService } from "./chatWebSocket";
import { newLeadWebSocketService } from "./newLeadWebSocket";
import { ExternalChatEndUser } from "./chatTypes";

export interface ChatToast {
    id: number;
    senderName: string;
    messagePreview: string;
    endUserId: number;
    channelKey: string;
    chatTypeKey?: string;
    platformKey?: string;
    threadRootMessageId?: number;
}

interface ChatNotificationContextValue {
    unreadCount: number;
    chatToastQueue: ChatToast[];
    removeChatToast: (id: number) => void;
    setActiveConversationId: (id: number | null) => void;
    registerMessageListener: (cb: (wsMessage: any) => void) => () => void;
    refetchUnreadCount: () => void;
}

const ChatNotificationContext = createContext<ChatNotificationContextValue>({
    unreadCount: 0,
    chatToastQueue: [],
    removeChatToast: () => { },
    setActiveConversationId: () => { },
    registerMessageListener: () => () => { },
    refetchUnreadCount: () => { },
});

export const useChatNotificationContext = () =>
    useContext(ChatNotificationContext);

let toastIdCounter = 0;

export function ChatNotificationProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const { auth, selectedTenantId } = useContext(AuthContext);
    const userId = auth?.user_id;

    const [unreadCount, setUnreadCount] = useState(0);
    const [chatToastQueue, setChatToastQueue] = useState<ChatToast[]>([]);

    useEffect(() => {
        if (!("setAppBadge" in navigator)) return;
        if (unreadCount > 0) {
            (navigator as any).setAppBadge(unreadCount);
        } else {
            (navigator as any).clearAppBadge();
        }
    }, [unreadCount]);

    const activeConversationIdRef = useRef<number | null>(null);
    const lastKnownMaxMessageIdRef = useRef<number>(0);
    const lastKnownMaxMessageIdInitializedRef = useRef<boolean>(false);
    const externalListenersRef = useRef<((wsMessage: any) => void)[]>([]);
    const conversationsCacheRef = useRef<ExternalChatEndUser[]>([]);
    const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchUnreadCount = useCallback(() => {
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        fetchTimeoutRef.current = setTimeout(async () => {
            if (!selectedTenantId) {
                setUnreadCount(0);
                return;
            }
            try {
                const currentTenant = auth?.tenants?.find(
                    (t: any) => String(t.tenant_id) === String(selectedTenantId)
                );
                const currentUserRole = currentTenant?.role_key;

                const response = await ChatService.getConversations(
                    Number(selectedTenantId),
                    userId,
                    { current_user_role: currentUserRole, created_by: userId, show_unread_count: true, limit: 1 }
                );
                
                if (response.results) {
                    const newResults = response.results.filter(r => 
                        !conversationsCacheRef.current.some(c => c.external_chat_end_user_id === r.external_chat_end_user_id)
                    );
                    conversationsCacheRef.current = [...conversationsCacheRef.current, ...newResults];
                }

                const readEndUserIds = response.results
                    .filter((c: any) => !c.unread_count || c.unread_count === 0)
                    .map((c: any) => c.external_chat_end_user_id);
                
                setChatToastQueue((prev) => 
                    prev.filter((toast) => !readEndUserIds.includes(toast.endUserId))
                );

                const count = response.total_unread ?? 0;
                setUnreadCount(count);

                if (!lastKnownMaxMessageIdInitializedRef.current) {
                    const maxId = response.results.reduce((max: number, conv: any) => {
                        const msgId = (conv.last_message as any)?.external_chat_message_id ?? 0;
                        return msgId > max ? msgId : max;
                    }, 0);
                    lastKnownMaxMessageIdRef.current = maxId;
                    lastKnownMaxMessageIdInitializedRef.current = true;
                }
            } catch {
                setUnreadCount(0);
            }
        }, 100);
    }, [selectedTenantId, userId, auth]);

    const resolveEndUserName = (wsMessage: any, endUserId: number): string => {
        const cached = conversationsCacheRef.current.find(
            (c) => c.external_chat_end_user_id === endUserId
        );
        if (cached) {
            const name = [cached.first_name, cached.last_name].filter(Boolean).join(" ").trim();
            if (name) return name;
        }
        return (
            wsMessage.end_user_first_name ||
            wsMessage.end_user_name ||
            wsMessage.sender_name ||
            "New Message"
        );
    };

    const resolvePreview = (wsMessage: any): string => {
        const pktKey = wsMessage.external_chat_packet_type?.external_chat_packet_type_key
            || wsMessage.message?.external_chat_packet_type?.external_chat_packet_type_key;

        const hasGoogleChatMeetingLink = !!(wsMessage.google_chat_meeting_link || wsMessage.message?.google_chat_meeting_link);
        const hasGoogleChatCard = !!(wsMessage.google_chat_card || wsMessage.message?.google_chat_card);
        const effectivePktKey = (pktKey === "TEXT" && hasGoogleChatMeetingLink)
            ? "GOOGLE_CHAT_MEETING_LINK"
            : (pktKey === "TEXT" && hasGoogleChatCard)
                ? "GOOGLE_CHAT_CARD"
                : pktKey;

        switch (effectivePktKey) {
            case "AUDIO": return "Audio";
            case "VIDEO": return "Video";
            case "IMAGE": return "Image";
            case "PDF": return "PDF File";
            case "FILE":
            case "FILE_RECEIVED":
            case "APPLICATION":
            case "DOCUMENT": return "File";
            case "OPTIONS": return "Options";
            case "CAROUSEL": return "Carousel";
            case "STICKER": return "Sticker";
            case "GOOGLE_CHAT_MEETING_LINK": return "You have got a meeting invite";
            case "GOOGLE_CHAT_CARD": return "Google Chat Card";
        }

        return wsMessage.message_text ||
            (typeof wsMessage.message === "string"
                ? wsMessage.message
                : wsMessage.message?.external_chat_message_value) ||
            "You have a new message";
    };

    const resolveChannelKey = (wsMessage: any): string =>
        wsMessage.external_chat_channel_key ||
        wsMessage.message?.external_chat_channel?.external_chat_channel_key ||
        wsMessage.channel_key ||
        "default";

    const setActiveConversationId = useCallback((id: number | null) => {
        activeConversationIdRef.current = id;
    }, []);

    const removeChatToast = useCallback((id: number) => {
        setChatToastQueue((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const registerMessageListener = useCallback(
        (cb: (wsMessage: any) => void) => {
            externalListenersRef.current.push(cb);
            return () => {
                externalListenersRef.current = externalListenersRef.current.filter(
                    (l) => l !== cb
                );
            };
        },
        []
    );

    const fetchUnreadCountRef = useRef(fetchUnreadCount);
    useEffect(() => {
        fetchUnreadCountRef.current = fetchUnreadCount;
    }, [fetchUnreadCount]);

    useEffect(() => {
        if (!selectedTenantId || !userId) return;

        lastKnownMaxMessageIdInitializedRef.current = false;
        lastKnownMaxMessageIdRef.current = 0;

        chatWebSocketService.connect(Number(selectedTenantId), Number(userId));
        newLeadWebSocketService.connect(Number(selectedTenantId));

        fetchUnreadCountRef.current();

        const handleMessage = (wsMessage: any) => {
            externalListenersRef.current.forEach((cb) => cb(wsMessage));

            if (wsMessage.user_ids && Array.isArray(wsMessage.user_ids)) {
                if (!wsMessage.user_ids.includes(Number(userId))) return;
            }

            if (wsMessage.type === "new_external_chat_message") {
                const incomingMsgId: number = wsMessage.external_chat_message_id ?? 0;
                const isNew =
                    lastKnownMaxMessageIdInitializedRef.current &&
                    incomingMsgId > lastKnownMaxMessageIdRef.current;

                if (isNew) {
                    lastKnownMaxMessageIdRef.current = incomingMsgId;
                    const incomingEndUserId: number = wsMessage.external_chat_end_user_id;
                    const activeId = activeConversationIdRef.current;
                    const isViewingThisChat =
                        activeId && incomingEndUserId && Number(incomingEndUserId) === Number(activeId);

                    const packetTypeKey =
                        wsMessage.external_chat_platform_packet_type ||
                        wsMessage.message?.external_chat_platform_packet_type?.external_chat_platform_packet_type_key;
                    const isAgentMessage = packetTypeKey === "AGENT_MESSAGE";

                    if (isAgentMessage) {
                        fetchUnreadCountRef.current();
                        return;
                    }

                    if (isViewingThisChat) {
                        new Audio("/open-chat.mp3").play().catch(() => { });
                    } else {
                        new Audio("/notification.mp3").play().catch(() => { });

                        const senderName = resolveEndUserName(wsMessage, incomingEndUserId);
                        const preview = resolvePreview(wsMessage);
                        const channelKey = resolveChannelKey(wsMessage);
                        const chatTypeKey = wsMessage.message?.chat_type?.chat_type_key;
                        const platformKey = wsMessage.message?.external_chat_platform?.external_chat_platform_key || wsMessage.external_chat_platform_key;

                        const hasParent = wsMessage.message?.has_parent === true;
                        const rootMsgId = hasParent ? (wsMessage.message?.root_message_id ?? undefined) : undefined;

                        toastIdCounter += 1;
                        const toast: ChatToast = {
                            id: toastIdCounter,
                            senderName,
                            messagePreview: preview,
                            endUserId: incomingEndUserId,
                            channelKey,
                            chatTypeKey,
                            platformKey,
                            threadRootMessageId: rootMsgId,
                        };
                        setChatToastQueue((prev) => [...prev.slice(-2), toast]);
                    }

                    fetchUnreadCountRef.current();
                }
            }
        };

        const handleNewLead = (wsMessage: any) => {
            externalListenersRef.current.forEach((cb) => cb(wsMessage));

            if (wsMessage.user_ids && Array.isArray(wsMessage.user_ids)) {
                if (!wsMessage.user_ids.includes(Number(userId))) return;
            }

            const incomingMsgId: number = wsMessage.external_chat_message_id ?? 0;
            const isNew =
                lastKnownMaxMessageIdInitializedRef.current &&
                incomingMsgId > lastKnownMaxMessageIdRef.current;

            if (isNew) {
                lastKnownMaxMessageIdRef.current = incomingMsgId;
                const incomingEndUserId: number = wsMessage.external_chat_end_user_id;
                const activeId = activeConversationIdRef.current;
                const isViewingThisChat =
                    activeId && incomingEndUserId && Number(incomingEndUserId) === Number(activeId);

                const packetTypeKey =
                    wsMessage.external_chat_platform_packet_type ||
                    wsMessage.message?.external_chat_platform_packet_type?.external_chat_platform_packet_type_key;
                const isAgentMessage = packetTypeKey === "AGENT_MESSAGE";

                if (isAgentMessage) {
                    fetchUnreadCountRef.current();
                    return;
                }

                if (!isViewingThisChat) {
                    new Audio("/notification.mp3").play().catch(() => { });

                    const senderName = resolveEndUserName(wsMessage, incomingEndUserId);
                    const preview = resolvePreview(wsMessage);
                    const channelKey = resolveChannelKey(wsMessage);
                    const chatTypeKey = wsMessage.message?.chat_type?.chat_type_key;
                    const platformKey = wsMessage.message?.external_chat_platform?.external_chat_platform_key || wsMessage.external_chat_platform_key;

                    const hasParent = wsMessage.message?.has_parent === true;
                    const rootMsgId = hasParent ? (wsMessage.message?.root_message_id ?? undefined) : undefined;

                    toastIdCounter += 1;
                    const toast: ChatToast = {
                        id: toastIdCounter,
                        senderName,
                        messagePreview: preview,
                        endUserId: incomingEndUserId,
                        channelKey,
                        chatTypeKey,
                        platformKey,
                        threadRootMessageId: rootMsgId,
                    };
                    setChatToastQueue((prev) => [...prev.slice(-2), toast]);
                }

                fetchUnreadCountRef.current();
            }
        };

        chatWebSocketService.onMessage(handleMessage);
        newLeadWebSocketService.onMessage(handleNewLead);

        return () => {
            chatWebSocketService.offMessage(handleMessage);
            chatWebSocketService.disconnect();
            newLeadWebSocketService.offMessage(handleNewLead);
            newLeadWebSocketService.disconnect();
        };
    }, [selectedTenantId, userId]);

    return (
        <ChatNotificationContext.Provider
            value={{
                unreadCount,
                chatToastQueue,
                removeChatToast,
                setActiveConversationId,
                registerMessageListener,
                refetchUnreadCount: fetchUnreadCount,
            }}
        >
            {children}
        </ChatNotificationContext.Provider>
    );
}
